import { Component } from "@theme/component";
import { cartPerformance } from "@theme/performance";
import { debounce, fetchConfig } from "@theme/utilities";

const SECTION_ID_PREFIX = "shopify-section-";
/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * A custom element that manages cart attributes form.
 * Handles auto-saving cart attributes with debounced updates on every input change.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} [form] - The cart attributes form element.
 *
 * @extends {Component<Refs>}
 */
export class CartAttributesForm extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Set<string>} */
  #changedFields = new Set();

  /** @type {Record<string, any>} */
  settings = {};

  /** @type {any | null} */
  pikadayInstance = null;

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
    this.initializeValidation();
    this.initializeDeliveryMethod();
    this.settings = JSON.parse(this.dataset.settings || "{}");
    console.log("CartAttributesForm settings:", this.settings);

    // Set initial checkout button state based on form validity
    this.updateCheckoutButtonState();

    // Initialize delivery prices based on current emirate
    const currentEmirate = this.getSelectedEmirate();
    if (currentEmirate) {
      this.updateDeliveryPrices(currentEmirate);
    }

    // Listen for cart update events to reinitialize delivery options
    document.addEventListener("cart:update", this.handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    if (this.pikadayInstance) {
      this.pikadayInstance.destroy();
      this.pikadayInstance = null;
    }
    // Remove cart update event listener
    document.removeEventListener("cart:update", this.handleCartUpdate);
  }

  /**
   * Setup event listeners for form inputs with auto-save.
   */
  setupEventListeners() {
    const form = this.getForm();
    const inputs = this.getInputs();

    if (!form) return;

    // Add input change listeners for auto-save
    inputs?.forEach((input) => {
      input.addEventListener("input", this.handleInputChange);
      input.addEventListener("change", this.handleInputChange);
    });

    // Prevent form submission since we auto-save
    form.addEventListener("submit", (event) => {
      event.preventDefault();
    });
  }

  initializeValidation() {
    this.setupDeliveryDateLogic();
    this.setupDeliveryOptionToggle();

    const dateInput = this.querySelector("#delivery_date");
    if (dateInput instanceof HTMLInputElement) {
      dateInput.addEventListener("change", this.validateDateInput);
    }

    const senderPhone = this.querySelector("#sender_phone");
    if (senderPhone instanceof HTMLInputElement) {
      senderPhone.addEventListener("input", this.formatPhoneNumber);
    }
  }

  /**
   * Setup delivery option toggle for calendar visibility.
   */
  setupDeliveryOptionToggle() {
    const deliveryOptions = this.querySelectorAll(
      'input[name="attributes[Delivery Option]"]',
    );

    deliveryOptions.forEach((radio) => {
      radio.addEventListener(
        "change",
        this.handleDeliveryOptionChange.bind(this),
      );
    });

    // Initialize calendar visibility and date based on current selection
    this.handleDeliveryOptionChange();
  }

  /**
   * Handle delivery option change - toggle calendar visibility and set date.
   */
  async handleDeliveryOptionChange() {
    await this.toggleCalendarVisibility();
    await this.setDateBasedOnDeliveryOption();
  }

  /**
   * Set delivery date based on selected delivery option.
   */
  async setDateBasedOnDeliveryOption() {
    const sameDayRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#same_day")
    );
    const tomorrowRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#tomorrow")
    );
    const calendarRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#calendar")
    );
    const dateInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#delivery_date")
    );

    if (!dateInput) return;

    // Get today in UAE timezone
    const now = new Date();
    const uaeToday = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
    );
    uaeToday.setHours(0, 0, 0, 0);

    if (sameDayRadio && sameDayRadio.checked) {
      // Set to today's date
      const year = uaeToday.getFullYear();
      const month = String(uaeToday.getMonth() + 1).padStart(2, "0");
      const day = String(uaeToday.getDate()).padStart(2, "0");
      const todayDate = `${year}-${month}-${day}`;

      dateInput.value = todayDate;
      dateInput.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (tomorrowRadio && tomorrowRadio.checked) {
      // Set to tomorrow's date
      const tomorrow = new Date(uaeToday);
      tomorrow.setDate(uaeToday.getDate() + 1);

      const year = tomorrow.getFullYear();
      const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
      const day = String(tomorrow.getDate()).padStart(2, "0");
      const tomorrowDate = `${year}-${month}-${day}`;

      dateInput.value = tomorrowDate;
      dateInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
    // If calendar is selected, keep the existing date or wait for user selection
    // The date will be set by Pikaday when user selects a date
  }

  /**
   * Toggle calendar-toggle visibility based on selected delivery option.
   */
  async toggleCalendarVisibility() {
    const calendarRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#calendar")
    );
    const calendarToggle = this.querySelector(".calendar-toggle");

    if (!calendarToggle) return;

    if (calendarRadio && calendarRadio.checked) {
      calendarToggle.style.display = "grid";
      // Initialize Pikaday date picker
      await this.initializePikaday();
    } else {
      calendarToggle.style.display = "none";
      // Destroy Pikaday instance when calendar option is not selected
      if (this.pikadayInstance) {
        this.pikadayInstance.destroy();
        this.pikadayInstance = null;
      }
    }
  }

  /**
   * Initialize Pikaday date picker with dynamic date disabling.
   */
  async initializePikaday() {
    // Check if Pikaday is available
    if (typeof Pikaday === "undefined") {
      console.error("Pikaday library is not loaded");
      return;
    }

    const dateInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#delivery_date")
    );

    if (!dateInput) return;

    // Destroy existing instance if any
    if (this.pikadayInstance) {
      this.pikadayInstance.destroy();
      this.pikadayInstance = null;
    }

    // Calculate minimum delivery days
    const minDays = await this.calculateMinimumDeliveryDays();

    // Get today in UAE timezone
    const now = new Date();
    const uaeToday = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
    );
    uaeToday.setHours(0, 0, 0, 0);

    // Calculate min date (today + minDays)
    const minDate = new Date(uaeToday);
    minDate.setDate(uaeToday.getDate() + minDays);

    // Calculate max date (today + 3 months)
    const maxDate = new Date(uaeToday);
    maxDate.setMonth(uaeToday.getMonth() + 3);

    // Get selected emirate to check Sunday delivery setting
    const selectedEmirate = this.getSelectedEmirate();
    let sundayDeliveryEnabled = true;

    // Check if Sunday delivery is enabled for the selected emirate
    switch (selectedEmirate) {
      case "Dubai":
        sundayDeliveryEnabled =
          this.settings?.enable_sunday_delivery_dubai !== false;
        break;
      case "Sharjah":
        sundayDeliveryEnabled =
          this.settings?.enable_sunday_delivery_sharjah !== false;
        break;
      case "Abu Dhabi":
        sundayDeliveryEnabled =
          this.settings?.enable_sunday_delivery_abu_dhabi !== false;
        break;
      case "Ajman":
        sundayDeliveryEnabled =
          this.settings?.enable_sunday_delivery_ajman !== false;
        break;
      case "Umm Al-Quwain":
        sundayDeliveryEnabled =
          this.settings?.enable_sunday_delivery_umm_al_quwain !== false;
        break;
      case "Ras Al Khaimah":
        sundayDeliveryEnabled =
          this.settings?.enable_sunday_delivery_ras_al_khaimah !== false;
        break;
      case "Fujairah":
        sundayDeliveryEnabled =
          this.settings?.enable_sunday_delivery_fujairah !== false;
        break;
      default:
        sundayDeliveryEnabled = true;
    }

    // Initialize Pikaday
    this.pikadayInstance = new Pikaday({
      field: dateInput,
      format: "YYYY-MM-DD",
      minDate: minDate,
      maxDate: maxDate,
      firstDay: 1, // Monday
      showDaysInNextAndPreviousMonths: true,
      enableSelectionDaysInNextAndPreviousMonths: false,
      disableDayFn: (date) => {
        // Disable dates before minDate
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);

        // Disable if before minimum date
        if (checkDate < minDate) {
          return true;
        }

        // Disable if after maximum date
        if (checkDate > maxDate) {
          return true;
        }

        // Disable Sundays if Sunday delivery is disabled for this emirate
        if (!sundayDeliveryEnabled && checkDate.getDay() === 0) {
          return true;
        }

        return false;
      },
      onSelect: (date) => {
        // Format date as YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const formattedDate = `${year}-${month}-${day}`;

        // Pikaday already updates the input value, just trigger save and delivery method update
        this.updateDeliveryMethod(formattedDate);
        this.debouncedSave(new Event("change"));
      },
    });

    // If there's already a value, set it in Pikaday
    if (dateInput.value) {
      this.pikadayInstance.setDate(dateInput.value, true);
    }
  }

  /**
   * Handle cart update events to reinitialize delivery options.
   * @param {Event} event - The cart update event.
   */
  handleCartUpdate = async (event) => {
    console.log("Cart updated, reinitializing delivery options");

    // Get current emirate
    const currentEmirate = this.getSelectedEmirate();
    if (!currentEmirate) return;

    // Update delivery prices based on new cart total
    this.updateDeliveryPrices(currentEmirate);

    // Recalculate minimum delivery days and update options visibility
    const minDays = await this.calculateMinimumDeliveryDays();
    this.updateDeliveryOptionsVisibility(minDays);

    // Update date constraints if needed
    await this.updateDeliveryDateConstraints();
  };

  /**
   * Handle input changes with auto-save.
   * @param {Event} event - The input event.
   */
  handleInputChange = (event) => {
    if (
      !(
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLSelectElement ||
        event.target instanceof HTMLTextAreaElement
      )
    )
      return;

    const fieldName = event.target.name;
    if (fieldName) {
      this.#changedFields.add(fieldName);
    }

    // Clear any error styling
    event.target.style.borderColor = "#ccc";

    // Update delivery date constraints if emirate changes
    if (event.target.id === "delivery_area") {
      this.updateDeliveryDateConstraints();
      this.updateDeliveryPrices(event.target.value);
    }

    // Update delivery method if delivery date changes
    if (event.target.id === "delivery_date") {
      this.updateDeliveryMethod(event.target.value);
    }

    // Auto-save with debounce
    this.debouncedSave(event);
  };

  /**
   * Debounced save function for auto-saving cart attributes.
   * @param {Event} event - The input event.
   */
  debouncedSave = debounce(async (event) => {
    await this.saveCartAttributes(event);
  }, 1000); // 1 second debounce

  /**
   * Validate date input to prevent past dates.
   * @param {Event} event - The change event.
   */
  validateDateInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const selectedDate = new Date(event.target.value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      const todayString = new Date().toISOString().split("T")[0];
      if (event.target instanceof HTMLInputElement && todayString) {
        event.target.value = todayString;
      }
      this.showMessage("error", "Please select a future date for delivery.");
    }
  };

  /**
   * Format phone number input.
   * @param {Event} event - The input event.
   */
  formatPhoneNumber = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    // Remove non-phone characters
    let value = event.target.value.replace(/[^\d+\-\s\(\)]/g, "");
    event.target.value = value;
  };

  /**
   * Setup delivery date logic based on emirate selection.
   */
  setupDeliveryDateLogic() {
    const dateInput = this.querySelector("#delivery_date");
    const emirateSelect = this.querySelector("#delivery_area");

    if (!dateInput || !emirateSelect) return;

    // Set initial date constraints
    this.updateDeliveryDateConstraints();
  }

  /**
   * Check if cart has personalized items by checking for specific properties.
   * @returns {Promise<boolean>} - Whether cart has personalized items.
   */
  async hasPersonalizedItems() {
    try {
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      // Check if any line item has personalized properties
      return cart.items.some(
        /** @param {any} item */ (item) => {
          return (
            item.properties &&
            (item.properties.personalized_text ||
              item.properties.font_selection ||
              item.properties.uploaded_images ||
              item.properties.card_selection ||
              Object.keys(item.properties).some(
                (key) =>
                  key.includes("personalized") ||
                  key.includes("custom") ||
                  key.includes("image") ||
                  key.includes("text"),
              ))
          );
        },
      );
    } catch (error) {
      console.warn("Could not check for personalized items:", error);
      return false; // Default to false if we can't check
    }
  }

  /**
   * Get selected emirate from delivery area dropdown.
   * @returns {string} - Selected emirate.
   */
  getSelectedEmirate() {
    const emirateSelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector("#delivery_area")
    );
    return emirateSelect ? emirateSelect.value || "Dubai" : "Dubai";
  }

  /**
   * Get current time hour in UAE timezone.
   * @returns {number} - Current hour (0-23) in UAE time.
   */
  getCurrentTimeHour() {
    // Get current UAE time (UTC+4)
    const now = new Date();
    const uaeTime = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
    );
    return uaeTime.getHours();
  }

  /**
   * Calculate minimum delivery days based on emirate and personalized items.
   * @returns {Promise<number>} - Minimum number of days.
   */
  async calculateMinimumDeliveryDays() {
    const emirate = this.getSelectedEmirate();
    const hasPersonalized = await this.hasPersonalizedItems();
    const currentHour = this.getCurrentTimeHour();

    let minDays = 0;

    switch (emirate) {
      case "Dubai":
        if (hasPersonalized) {
          minDays = Number(this.settings?.dubai_personalized_delivery) || 3;
        } else {
          const sameDayCutoff = 12; // Same day cutoff at 12 PM
          const nextDayCutoff = Number(this.settings?.dubai_cuttoff_hour) || 16;
          const sameDayEnabled =
            this.settings?.enable_dubai_same_day_delivery !== false;
          const sundayDeliveryEnabled =
            this.settings?.enable_sunday_delivery_dubai !== false;

          // Check if today is Sunday (0 = Sunday in JavaScript)
          const now = new Date();
          const uaeTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
          );
          const isSunday = uaeTime.getDay() === 0;

          if (
            currentHour < sameDayCutoff &&
            sameDayEnabled &&
            (!isSunday || sundayDeliveryEnabled)
          ) {
            minDays = 0; // Same day delivery
          } else if (currentHour < nextDayCutoff) {
            minDays = 1; // Next day delivery
          } else {
            minDays = 2; // After next day cutoff
          }
        }
        break;
      case "Sharjah":
        if (hasPersonalized) {
          minDays = Number(this.settings?.sharjah_personalized_delivery) || 3;
        } else {
          const sameDayCutoff = 12; // Same day cutoff at 12 PM
          const nextDayCutoff =
            Number(this.settings?.sharjah_cuttoff_hour) || 16;
          const sameDayEnabled =
            this.settings?.enable_sharjah_same_day_delivery !== false;
          const sundayDeliveryEnabled =
            this.settings?.enable_sunday_delivery_sharjah !== false;

          // Check if today is Sunday (0 = Sunday in JavaScript)
          const now = new Date();
          const uaeTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
          );
          const isSunday = uaeTime.getDay() === 0;

          if (
            currentHour < sameDayCutoff &&
            sameDayEnabled &&
            (!isSunday || sundayDeliveryEnabled)
          ) {
            minDays = 0; // Same day delivery
          } else if (currentHour < nextDayCutoff) {
            minDays = 1; // Next day delivery
          } else {
            minDays = 2; // After next day cutoff
          }
        }
        break;
      case "Abu Dhabi":
        if (hasPersonalized) {
          minDays = Number(this.settings?.abu_dhabi_personalized_delivery) || 3;
        } else {
          const nextDayCutoff =
            Number(this.settings?.abu_dhabi_cuttoff_hour) || 16;
          const sundayDeliveryEnabled =
            this.settings?.enable_sunday_delivery_abu_dhabi !== false;

          // Check if today is Sunday (0 = Sunday in JavaScript)
          const now = new Date();
          const uaeTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
          );
          const isSunday = uaeTime.getDay() === 0;

          // If it's Sunday and Sunday delivery is disabled, add 1 day
          if (isSunday && !sundayDeliveryEnabled) {
            minDays = currentHour >= nextDayCutoff ? 3 : 2;
          } else {
            minDays = currentHour >= nextDayCutoff ? 2 : 1;
          }
        }
        break;
      case "Ajman":
        if (hasPersonalized) {
          minDays = Number(this.settings?.ajman_personalized_delivery) || 3;
        } else {
          const nextDayCutoff = Number(this.settings?.ajman_cuttoff_hour) || 16;
          const sundayDeliveryEnabled =
            this.settings?.enable_sunday_delivery_ajman !== false;

          // Check if today is Sunday (0 = Sunday in JavaScript)
          const now = new Date();
          const uaeTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
          );
          const isSunday = uaeTime.getDay() === 0;

          // If it's Sunday and Sunday delivery is disabled, add 1 day
          if (isSunday && !sundayDeliveryEnabled) {
            minDays = currentHour >= nextDayCutoff ? 3 : 2;
          } else {
            minDays = currentHour >= nextDayCutoff ? 2 : 1;
          }
        }
        break;
      case "Umm Al-Quwain":
        if (hasPersonalized) {
          minDays =
            Number(this.settings?.umm_al_quwain_personalized_delivery) || 3;
        } else {
          const nextDayCutoff =
            Number(this.settings?.umm_al_quwain_cuttoff_hour) || 16;
          const sundayDeliveryEnabled =
            this.settings?.enable_sunday_delivery_umm_al_quwain !== false;

          // Check if today is Sunday (0 = Sunday in JavaScript)
          const now = new Date();
          const uaeTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
          );
          const isSunday = uaeTime.getDay() === 0;

          // If it's Sunday and Sunday delivery is disabled, add 1 day
          if (isSunday && !sundayDeliveryEnabled) {
            minDays = currentHour >= nextDayCutoff ? 3 : 2;
          } else {
            minDays = currentHour >= nextDayCutoff ? 2 : 1;
          }
        }
        break;
      case "Ras Al Khaimah":
        if (hasPersonalized) {
          minDays =
            Number(this.settings?.ras_al_khaimah_personalized_delivery) || 3;
        } else {
          const nextDayCutoff =
            Number(this.settings?.ras_al_khaimah_cuttoff_hour) || 16;
          const sundayDeliveryEnabled =
            this.settings?.enable_sunday_delivery_ras_al_khaimah !== false;

          // Check if today is Sunday (0 = Sunday in JavaScript)
          const now = new Date();
          const uaeTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
          );
          const isSunday = uaeTime.getDay() === 0;

          // If it's Sunday and Sunday delivery is disabled, add 1 day
          if (isSunday && !sundayDeliveryEnabled) {
            minDays = currentHour >= nextDayCutoff ? 3 : 2;
          } else {
            minDays = currentHour >= nextDayCutoff ? 2 : 1;
          }
        }
        break;
      case "Fujairah":
        if (hasPersonalized) {
          minDays = Number(this.settings?.fujairah_personalized_delivery) || 3;
        } else {
          const nextDayCutoff =
            Number(this.settings?.fujairah_cuttoff_hour) || 16;
          const sundayDeliveryEnabled =
            this.settings?.enable_sunday_delivery_fujairah !== false;

          // Check if today is Sunday (0 = Sunday in JavaScript)
          const now = new Date();
          const uaeTime = new Date(
            now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
          );
          const isSunday = uaeTime.getDay() === 0;

          // If it's Sunday and Sunday delivery is disabled, add 1 day
          if (isSunday && !sundayDeliveryEnabled) {
            minDays = currentHour >= nextDayCutoff ? 3 : 2;
          } else {
            minDays = currentHour >= nextDayCutoff ? 2 : 1;
          }
        }
        break;
      default:
        minDays = hasPersonalized ? 3 : currentHour >= 16 ? 2 : 1;
    }

    return minDays;
  }

  /**
   * Calculate minimum delivery date based on emirate and personalized items.
   * @returns {Promise<string>} - Minimum date in YYYY-MM-DD format.
   */
  async calculateMinimumDeliveryDate() {
    const minDays = await this.calculateMinimumDeliveryDays();

    // Get current date in UAE timezone
    const now = new Date();
    const uaeToday = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
    );

    const minDate = new Date(uaeToday);
    minDate.setDate(uaeToday.getDate() + minDays);

    return minDate.toISOString().split("T")[0] || "";
  }

  /**
   * Update delivery options visibility based on minimum delivery days.
   * @param {number} minDays - Minimum delivery days.
   */
  updateDeliveryOptionsVisibility(minDays) {
    const sameDayOption = this.querySelector(".delivery-option:has(#same_day)");
    const tomorrowOption = this.querySelector(
      ".delivery-option:has(#tomorrow)",
    );
    const calendarOption = this.querySelector(
      ".delivery-option:has(#calendar)",
    );

    if (!sameDayOption || !tomorrowOption || !calendarOption) return;

    // Reset all to visible first
    sameDayOption.style.display = "flex";
    tomorrowOption.style.display = "flex";
    calendarOption.style.display = "flex";

    if (minDays === 0) {
      // Show all options: same_day, tomorrow, calendar
      sameDayOption.style.display = "flex";
      tomorrowOption.style.display = "flex";
      calendarOption.style.display = "flex";
    } else if (minDays === 1) {
      // Show only tomorrow and calendar
      sameDayOption.style.display = "none";
      tomorrowOption.style.display = "flex";
      calendarOption.style.display = "flex";

      // If same_day was selected, clear it and select tomorrow
      const sameDayRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector("#same_day")
      );
      if (sameDayRadio && sameDayRadio.checked) {
        sameDayRadio.checked = false;
        const tomorrowRadio = /** @type {HTMLInputElement | null} */ (
          this.querySelector("#tomorrow")
        );
        if (tomorrowRadio) {
          tomorrowRadio.checked = true;
          tomorrowRadio.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    } else {
      // minDays > 1: Show only calendar
      sameDayOption.style.display = "none";
      tomorrowOption.style.display = "none";
      calendarOption.style.display = "flex";

      // If same_day or tomorrow was selected, clear it and select calendar
      const sameDayRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector("#same_day")
      );
      const tomorrowRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector("#tomorrow")
      );
      const calendarRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector("#calendar")
      );

      if (
        (sameDayRadio && sameDayRadio.checked) ||
        (tomorrowRadio && tomorrowRadio.checked)
      ) {
        if (sameDayRadio) sameDayRadio.checked = false;
        if (tomorrowRadio) tomorrowRadio.checked = false;
        if (calendarRadio) {
          calendarRadio.checked = true;
          calendarRadio.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }
    }
  }

  /**
   * Update delivery date constraints based on selected emirate.
   */
  async updateDeliveryDateConstraints() {
    const dateInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#delivery_date")
    );
    const emirateSelect = /** @type {HTMLSelectElement | null} */ (
      this.querySelector("#delivery_area")
    );

    if (!dateInput || !emirateSelect) return;

    const emirate = this.getSelectedEmirate();

    // Disable date picker if no emirate is selected
    if (!emirate || emirate === "") {
      dateInput.disabled = true;
      dateInput.value = "";
      this.updateDeliveryHelperText("Please select an emirate first.");
      return;
    }

    // Enable date picker if emirate is selected
    dateInput.disabled = false;

    // Calculate minimum days and update delivery options visibility
    const minDays = await this.calculateMinimumDeliveryDays();
    this.updateDeliveryOptionsVisibility(minDays);

    const minDate = await this.calculateMinimumDeliveryDate();
    dateInput.setAttribute("min", minDate);

    // Clear the current value if it's before the minimum date
    if (dateInput.value && dateInput.value < minDate) {
      dateInput.value = "";
    }
    dateInput.dispatchEvent(new Event("change"));

    // Reinitialize Pikaday if calendar is selected
    const calendarRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#calendar")
    );
    if (calendarRadio && calendarRadio.checked && this.pikadayInstance) {
      await this.initializePikaday();
    }

    // Update helper text based on emirate and personalization
    await this.updateDeliveryHelperText();
  }

  /**
   * Update delivery helper text based on selected emirate and personalization.
   * @param {string} [customMessage] - Custom message to display.
   */
  async updateDeliveryHelperText(customMessage) {
    // Create or update helper text element
    let noteList = /** @type {HTMLElement | null} */ (
      this.querySelector(".notes-list")
    );
    const selectedEmirate = this.getSelectedEmirate();
    const noteListContent1 = `<li><strong>Same-day delivery</strong> → order needs to be placed before <strong>12 pm</strong></li>
                <li><strong>Next-day delivery</strong> → order needs to be placed before <strong>4 pm</strong></li>
                <li><strong>Personalized items</strong> → delivery + <strong>3 days</strong></li>`;
    const noteListContent2 = `
                <li><strong>Next-day delivery</strong> → order needs to be placed before <strong>4 pm</strong></li>
                <li><strong>Personalized items</strong> → delivery + <strong>3 days</strong></li>`;
    // based on selected emirate assign notelist html content
    if (noteList) {
      switch (selectedEmirate) {
        case "Dubai":
          noteList.innerHTML = noteListContent1;
          break;
        case "Sharjah":
          noteList.innerHTML = noteListContent1;
          break;
        case "Abu Dhabi":
          noteList.innerHTML = noteListContent2;
          break;
        case "Ajman":
          noteList.innerHTML = noteListContent2;
          break;
        case "Umm Al-Quwain":
          noteList.innerHTML = noteListContent2;
          break;
        case "Ras Al Khaimah":
          noteList.innerHTML = noteListContent2;
          break;
        case "Fujairah":
          noteList.innerHTML = noteListContent2;
          break;
        default:
          noteList.innerHTML = noteListContent2;
      }
    }
  }

  /**
   * Update delivery method based on selected delivery date.
   * @param {string} selectedDate - The selected delivery date in YYYY-MM-DD format.
   */
  updateDeliveryMethod(selectedDate) {
    if (!selectedDate) {
      // If no date is selected, set to default
      this.setDeliveryMethodValue("Standard Delivery");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0);

    let deliveryMethod = "Standard Delivery"; // Default value

    // Compare dates to determine delivery method
    if (selectedDateObj.getTime() === today.getTime()) {
      deliveryMethod = "Same Day Delivery";
    } else if (selectedDateObj.getTime() === tomorrow.getTime()) {
      deliveryMethod = "Standard Delivery";
    } else {
      deliveryMethod = "Standard Delivery";
    }

    this.setDeliveryMethodValue(deliveryMethod);
  }

  /**
   * Set the delivery method value in a hidden input or create one if it doesn't exist.
   * @param {string} method - The delivery method to set.
   */
  setDeliveryMethodValue(method) {
    let deliveryMethodInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="attributes[Delivery Method]"]')
    );

    if (!deliveryMethodInput) {
      // Create hidden input for delivery method if it doesn't exist
      deliveryMethodInput = document.createElement("input");
      deliveryMethodInput.type = "hidden";
      deliveryMethodInput.name = "attributes[Delivery Method]";

      const form = this.getForm();
      if (form) {
        form.appendChild(deliveryMethodInput);
      }
    }

    if (deliveryMethodInput) {
      const previousValue = deliveryMethodInput.value;
      deliveryMethodInput.value = method;

      // Add to changed fields for auto-save
      this.#changedFields.add("attributes[Delivery Method]");

      // Only update line item properties if the value actually changed
      if (previousValue !== method) {
        // Collect all delivery information from form
        const deliveryInfo = this.getDeliveryInfoFromForm();

        // Debounced update to avoid too many API calls
        this.debouncedLineItemUpdate(deliveryInfo);
      }
    }
  }

  /**
   * Get delivery information from form fields.
   * @returns {Object} The delivery information.
   */
  getDeliveryInfoFromForm() {
    const form = this.getForm();
    if (!form)
      return {
        deliveryMethod: "Standard Delivery",
        deliveryArea: "",
        deliveryOption: "",
        deliveryDate: "",
      };

    const deliveryMethodInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="attributes[Delivery Method]"]')
    );
    const deliveryAreaInput = /** @type {HTMLSelectElement | null} */ (
      form.querySelector('select[name="attributes[Delivery Area]"]')
    );
    const deliveryOptionInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      form.querySelectorAll('input[name="attributes[Delivery Option]"]')
    );
    const deliveryDateInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="attributes[Delivery Date]"]')
    );

    const selectedOption = Array.from(deliveryOptionInputs).find(
      (input) => input.checked,
    );

    return {
      deliveryMethod: deliveryMethodInput?.value || "Standard Delivery",
      deliveryArea: deliveryAreaInput?.value || "",
      deliveryOption: selectedOption?.value || "",
      deliveryDate: deliveryDateInput?.value || "",
    };
  }
  /**
   * Initialize delivery method based on current delivery date value.
   */
  async initializeDeliveryMethod() {
    const dateInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector("#delivery_date")
    );

    // Initialize delivery options visibility based on selected emirate
    const emirate = this.getSelectedEmirate();
    if (emirate && emirate !== "") {
      const minDays = await this.calculateMinimumDeliveryDays();
      this.updateDeliveryOptionsVisibility(minDays);
    }

    if (dateInput && dateInput.value) {
      // Update delivery method based on existing date value
      this.updateDeliveryMethod(dateInput.value);
    } else {
      // Set default delivery method
      this.setDeliveryMethodValue("Standard Delivery");
    }
  }

  /**
   * Update delivery prices based on selected emirate.
   * Prices are fetched from settings for each emirate.
   * @param {string} emirate - Selected emirate.
   */
  updateDeliveryPrices(emirate) {
    const sameDayPrice = this.querySelector("[data-price-same-day]");
    const tomorrowPrice = this.querySelector("[data-price-tomorrow]");
    const calendarPrice = this.querySelector("[data-price-calendar]");

    if (!tomorrowPrice || !calendarPrice) return;

    // Get prices from settings based on emirate
    let sameDayPriceValue = 0;
    let nextDayPriceValue = 0;
    let calendarPriceValue = 0;

    const cart_subtotalPrice =
      parseFloat(
        (
          document.querySelector("[data-cart-subtotal-price]")?.textContent ||
          "0"
        )
          .replace(/,/g, "")
          .trim(),
      ) || 0;

    switch (emirate) {
      case "Dubai":
        sameDayPriceValue = Number(this.settings?.dubai_same_day_price) || 0;
        nextDayPriceValue = Number(this.settings?.dubai_next_day_price) || 0;
        calendarPriceValue = Number(this.settings?.dubai_next_day_price) || 0;

        // Apply free delivery if minimum order is met
        const dubaiSameDayMin =
          Number(this.settings?.dubai_same_day_min_order) || 800;
        const dubaiNextDayMin =
          Number(this.settings?.dubai_next_day_min_order) || 350;

        if (cart_subtotalPrice >= dubaiSameDayMin) {
          sameDayPriceValue = 0;
        }
        if (cart_subtotalPrice >= dubaiNextDayMin) {
          nextDayPriceValue = 0;
          calendarPriceValue = 0;
        }
        break;
      case "Sharjah":
        sameDayPriceValue = Number(this.settings?.sharjah_same_day_price) || 0;
        nextDayPriceValue = Number(this.settings?.sharjah_next_day_price) || 0;
        calendarPriceValue = Number(this.settings?.sharjah_next_day_price) || 0;

        // Apply free delivery if minimum order is met
        const sharjahSameDayMin =
          Number(this.settings?.sharjah_same_day_min_order) || 800;
        const sharjahNextDayMin =
          Number(this.settings?.sharjah_next_day_min_order) || 350;

        if (cart_subtotalPrice >= sharjahSameDayMin) {
          sameDayPriceValue = 0;
        }
        if (cart_subtotalPrice >= sharjahNextDayMin) {
          nextDayPriceValue = 0;
          calendarPriceValue = 0;
        }
        break;
      case "Abu Dhabi":
        nextDayPriceValue =
          Number(this.settings?.standard_delivery_abu_dhabi) || 39;
        calendarPriceValue =
          Number(this.settings?.standard_delivery_abu_dhabi) || 39;

        // Apply free delivery if minimum order is met
        const abuDhabiMin =
          Number(this.settings?.abu_dhabi_min_order_for_free_delivery) || 350;
        if (cart_subtotalPrice >= abuDhabiMin) {
          nextDayPriceValue = 0;
          calendarPriceValue = 0;
        }
        break;
      case "Ajman":
        nextDayPriceValue =
          Number(this.settings?.standard_delivery_price_in_ajman) || 39;
        calendarPriceValue =
          Number(this.settings?.standard_delivery_price_in_ajman) || 39;

        // Apply free delivery if minimum order is met
        const ajmanMin =
          Number(this.settings?.ajman_min_order_for_free_delivery) || 350;
        if (cart_subtotalPrice >= ajmanMin) {
          nextDayPriceValue = 0;
          calendarPriceValue = 0;
        }
        break;
      case "Umm Al-Quwain":
        nextDayPriceValue =
          Number(this.settings?.standard_delivery_price_in_al_quwain) || 39;
        calendarPriceValue =
          Number(this.settings?.standard_delivery_price_in_al_quwain) || 39;

        // Apply free delivery if minimum order is met
        const ummAlQuwainMin =
          Number(this.settings?.umm_al_quwain_min_order_for_free_delivery) ||
          350;
        if (cart_subtotalPrice >= ummAlQuwainMin) {
          nextDayPriceValue = 0;
          calendarPriceValue = 0;
        }
        break;
      case "Ras Al Khaimah":
        nextDayPriceValue =
          Number(this.settings?.standard_delivery_ras_al_khaimah) || 39;
        calendarPriceValue =
          Number(this.settings?.standard_delivery_ras_al_khaimah) || 39;

        // Apply free delivery if minimum order is met
        const rasAlKhaimahMin =
          Number(this.settings?.ras_al_khaimah_min_order_for_free_delivery) ||
          350;
        if (cart_subtotalPrice >= rasAlKhaimahMin) {
          nextDayPriceValue = 0;
          calendarPriceValue = 0;
        }
        break;
      case "Fujairah":
        nextDayPriceValue =
          Number(this.settings?.standard_delivery_price_in_fujairah) || 39;
        calendarPriceValue =
          Number(this.settings?.standard_delivery_price_in_fujairah) || 39;

        // Apply free delivery if minimum order is met
        const fujairahMin =
          Number(this.settings?.fujairah_min_order_for_free_delivery) || 350;
        if (cart_subtotalPrice >= fujairahMin) {
          nextDayPriceValue = 0;
          calendarPriceValue = 0;
        }
        break;
      default:
        nextDayPriceValue = 39;
        calendarPriceValue = 39;
    }

    // Update same day price if element exists
    if (sameDayPrice) {
      if (sameDayPriceValue === 0) {
        sameDayPrice.textContent = "FREE";
        sameDayPrice.classList.add("free");
        sameDayPrice.classList.remove("paid");
      } else {
        sameDayPrice.textContent = `${sameDayPriceValue} aed`;
        sameDayPrice.classList.remove("free");
        sameDayPrice.classList.add("paid");
      }
    }

    // Update tomorrow/next day price
    if (nextDayPriceValue === 0) {
      tomorrowPrice.textContent = "FREE";
      tomorrowPrice.classList.add("free");
      tomorrowPrice.classList.remove("paid");
    } else {
      tomorrowPrice.textContent = `${nextDayPriceValue} aed`;
      tomorrowPrice.classList.remove("free");
      tomorrowPrice.classList.add("paid");
    }

    // Update calendar price
    if (calendarPriceValue === 0) {
      calendarPrice.textContent = "FREE";
      calendarPrice.classList.add("free");
      calendarPrice.classList.remove("paid");
    } else {
      calendarPrice.textContent = `${calendarPriceValue} aed`;
      calendarPrice.classList.remove("free");
      calendarPrice.classList.add("paid");
    }
  }

  /**
   * Debounced update for line item properties to avoid too many API calls.
   * @param {Object} deliveryInfo - The delivery information to update.
   */
  debouncedLineItemUpdate = debounce(async (deliveryInfo) => {
    await this.updateFirstLineItemProperties(deliveryInfo);
  }, 1000); // 1 second debounce, same as cart attributes

  /**
   * Update the first line item properties with delivery information using cart change API.
   * @param {Object} deliveryInfo - The delivery information to add as line item properties.
   * @param {string} deliveryInfo.deliveryMethod - The delivery method.
   * @param {string} deliveryInfo.deliveryArea - The delivery area/emirate.
   * @param {string} deliveryInfo.deliveryOption - The delivery option (Same Day, Tomorrow, Calendar).
   * @param {string} deliveryInfo.deliveryDate - The delivery date.
   */
  async updateFirstLineItemProperties(deliveryInfo) {
    if (!deliveryInfo || !deliveryInfo.deliveryMethod) return;

    try {
      // First, get current cart to check if there are line items
      const cartResponse = await fetch("/cart.js");
      const currentCart = await cartResponse.json();

      if (!currentCart.items || currentCart.items.length === 0) {
        console.log("No items in cart to update");
        return;
      }

      // Get the first line item
      const firstLineItem = currentCart.items[0];

      // Preserve existing properties and add delivery information
      const updatedProperties = {
        ...firstLineItem.properties,
        "Delivery Method": deliveryInfo.deliveryMethod,
        "Delivery Area": deliveryInfo.deliveryArea,
        "Delivery Option": deliveryInfo.deliveryOption,
        "Delivery Date": deliveryInfo.deliveryDate,
      };

      // Use cart change API to update the first line item (line: 1)
      const changeConfig = fetchConfig("json", {
        body: JSON.stringify({
          line: 1,
          properties: updatedProperties,
        }),
      });

      const changeResponse = await fetch("/cart/change.js", changeConfig);

      if (!changeResponse.ok) {
        throw new Error(`HTTP error! status: ${changeResponse.status}`);
      }

      const result = await changeResponse.json();
      console.log(
        "Successfully updated first line item with delivery information:",
        deliveryInfo,
      );

      // Dispatch event to notify other components
      this.dispatchEvent(
        new CustomEvent("cart:lineItemUpdated", {
          detail: {
            lineItem: result.items?.[0],
            deliveryInfo: deliveryInfo,
          },
          bubbles: true,
        }),
      );
    } catch (error) {
      console.error("Error updating first line item properties:", error);
    }
  }

  /**
   * Save cart attributes to Shopify cart with auto-save.
   * @param {Event} event - The triggering event.
   */
  async saveCartAttributes(event) {
    const form = this.getForm();
    if (!form) return;

    // Cancel any active requests
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    // Set loading state - disable checkout buttons
    this.setCheckoutButtonsLoadingState(true);

    try {
      // First, fetch current cart to preserve existing data
      const cartResponse = await fetch("/cart.js");
      const currentCart = await cartResponse.json();

      // Collect form data
      const formData = new FormData(form);

      // Get delivery option to determine how to set delivery date
      const deliveryOption = String(
        formData.get("attributes[Delivery Option]") || "",
      );

      // Calculate delivery date based on delivery option
      let deliveryDate = "";
      if (deliveryOption === "Same Day" || deliveryOption === "Tomorrow") {
        // Get today in UAE timezone
        const now = new Date();
        const uaeToday = new Date(
          now.toLocaleString("en-US", { timeZone: "Asia/Dubai" }),
        );
        uaeToday.setHours(0, 0, 0, 0);

        if (deliveryOption === "Same Day") {
          // Set to today's date
          const year = uaeToday.getFullYear();
          const month = String(uaeToday.getMonth() + 1).padStart(2, "0");
          const day = String(uaeToday.getDate()).padStart(2, "0");
          deliveryDate = `${year}-${month}-${day}`;
        } else if (deliveryOption === "Tomorrow") {
          // Set to tomorrow's date
          const tomorrow = new Date(uaeToday);
          tomorrow.setDate(uaeToday.getDate() + 1);

          const year = tomorrow.getFullYear();
          const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
          const day = String(tomorrow.getDate()).padStart(2, "0");
          deliveryDate = `${year}-${month}-${day}`;
        }
      } else {
        // For Calendar option, use the date from form data
        deliveryDate = String(formData.get("attributes[Delivery Date]") || "");
      }

      // Convert FormData to cart attributes object
      /** @type {Record<string, string>} */
      const newAttributes = {
        "Sender Name": String(formData.get("attributes[Sender Name]") || ""),
        "Sender Phone": String(formData.get("attributes[Sender Phone]") || ""),
        "Recipient Name": String(
          formData.get("attributes[Recipient Name]") || "",
        ),
        "Recipient Phone": String(
          formData.get("attributes[Recipient Phone]") || "",
        ),
        "Delivery Area": String(
          formData.get("attributes[Delivery Area]") || "",
        ),
        "Delivery Option": deliveryOption,
        "Delivery Date": deliveryDate,
        "Delivery Method": String(
          formData.get("attributes[Delivery Method]") || "Standard Delivery",
        ),
      };

      // Merge with existing attributes to preserve other attributes
      const mergedAttributes = {
        ...currentCart.attributes,
        ...newAttributes,
      };

      // Prepare request body with section rendering for cart-actions updates
      const sectionsToUpdate = new Set();

      // Find the parent section ID from cart components
      const cartItemsComponent = /** @type {HTMLElement | null} */ (
        document.querySelector("cart-items-component[data-section-id]")
      );
      if (cartItemsComponent && cartItemsComponent.dataset.sectionId) {
        sectionsToUpdate.add(cartItemsComponent.dataset.sectionId);
      }

      // Also include any other cart-related sections that might need updating
      const cartSummaryElements =
        document.querySelectorAll("[data-section-id]");
      cartSummaryElements.forEach((element) => {
        const htmlElement = /** @type {HTMLElement} */ (element);
        if (
          htmlElement.dataset.sectionId &&
          htmlElement.closest(
            ".cart-actions, .cart__summary-container, .main-cart",
          )
        ) {
          sectionsToUpdate.add(htmlElement.dataset.sectionId);
        }
      });

      const config = fetchConfig("json", {
        body: JSON.stringify({
          attributes: mergedAttributes,
          ...(sectionsToUpdate.size > 0 && {
            sections: Array.from(sectionsToUpdate).join(","),
          }),
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Update sections if sections were returned in response
      if (result.sections) {
        this.updateSections(result.sections);
      }

      // Clear changed fields
      this.#changedFields.clear();

      // Dispatch custom event for other components
      this.dispatchEvent(
        new CustomEvent("cart:attributesUpdated", {
          detail: { attributes: result.attributes || mergedAttributes },
          bubbles: true,
        }),
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error auto-saving cart attributes:", error);
      }
    } finally {
      this.#activeFetch = null;
      // Re-enable checkout buttons after save completes
      this.setCheckoutButtonsLoadingState(false);
      if (event) {
        cartPerformance.measureFromEvent(
          "attributes-update:user-action",
          event,
        );
      }
    }
  }

  /**
   * Check if all required cart attributes are filled.
   * @returns {boolean} - Whether all required fields are filled.
   */
  areAllRequiredFieldsFilled() {
    const form = this.getForm();
    if (!form) return false;

    const requiredFields = [
      "attributes[Delivery Area]",
      "attributes[Delivery Date]",
      "attributes[Delivery Option]",
      "attributes[Delivery Method]",
    ];

    return requiredFields.every((fieldName) => {
      const field = form.querySelector(`[name="${fieldName}"]`);
      if (
        field instanceof HTMLInputElement ||
        field instanceof HTMLSelectElement
      ) {
        return field.value.trim() !== "";
      }
      return false;
    });
  }

  /**
   * Update checkout button state based on form validity.
   */
  updateCheckoutButtonState() {
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], .cart__checkout-button, [data-checkout-button]',
    );

    const allFieldsFilled = this.areAllRequiredFieldsFilled();

    checkoutButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = !allFieldsFilled;
      }
    });
  }

  /**
   * Set loading state for checkout buttons during cart attribute updates.
   * @param {boolean} isLoading - Whether the cart is being updated.
   */
  setCheckoutButtonsLoadingState(isLoading) {
    // Find all checkout buttons in the cart
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], .cart__checkout-button, [data-checkout-button]',
    );

    checkoutButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        if (isLoading) {
          // Disable button and show loading state
          button.disabled = true;
          button.classList.add("loading");

          // Store original text if not already stored
          if (!button.hasAttribute("data-original-text")) {
            button.setAttribute("data-original-text", button.textContent || "");
          }

          button.textContent = "Updating...";
          console.log("Checkout button disabled during cart update");
        } else {
          // Remove loading state and update based on form validity
          button.classList.remove("loading");

          // Restore original text
          const originalText = button.getAttribute("data-original-text");
          if (originalText) {
            button.textContent = originalText;
            button.removeAttribute("data-original-text");
          }

          // Update button state based on whether all fields are filled
          this.updateCheckoutButtonState();

          console.log(
            "Checkout button loading state cleared after cart update",
          );
        }
      }
    });
  }

  /**
   * Update page sections with fresh content from server.
   * @param {Record<string, string>} sections - Section HTML content.
   */
  updateSections(sections) {
    try {
      Object.entries(sections).forEach(([sectionId, sectionHtml]) => {
        // console.log(`Updating section: ${sectionId}`);
        // console.log(`Updating section html: ${sectionHtml}`);

        // Use morphSection to update the section content
        // This will find the section by ID and update it with new content
        const fragment = new DOMParser().parseFromString(
          sectionHtml,
          "text/html",
        );
        const existingElement = document.getElementById(
          buildSectionSelector(sectionId),
        );
        const newElement = fragment.getElementById(
          buildSectionSelector(sectionId),
        );

        if (existingElement && newElement) {
          const currentCartSummary = existingElement.querySelector(
            ".cart-page__summary",
          );
          const newCartSummary = newElement.querySelector(
            ".cart-page__summary",
          );
          if (currentCartSummary && newCartSummary) {
            currentCartSummary.innerHTML = newCartSummary.innerHTML;
          }
        }
      });
    } catch (error) {
      console.error("Error updating sections:", error);
    }
  }

  /**
   * Show success or error message.
   * @param {string} type - Message type ('success' or 'error').
   * @param {string} message - Message text.
   */
  showMessage(type, message) {
    // Remove any existing messages
    const existingMessage = this.querySelector(".form-message");
    if (existingMessage) {
      existingMessage.remove();
    }

    // Create new message element
    const messageElement = document.createElement("div");
    messageElement.className = `form-message form-message--${type}`;
    messageElement.textContent = message;

    // Style the message
    Object.assign(messageElement.style, {
      padding: "10px",
      marginTop: "10px",
      borderRadius: "4px",
      fontSize: "14px",
      textAlign: "center",
      backgroundColor: type === "success" ? "#d4edda" : "#f8d7da",
      color: type === "success" ? "#155724" : "#721c24",
      border: `1px solid ${type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
    });

    // Insert message after form
    const form = this.getForm();
    if (form) {
      form.parentNode?.insertBefore(messageElement, form.nextSibling);
    }

    // Auto-remove message
    setTimeout(
      () => {
        messageElement.remove();
      },
      type === "success" ? 3000 : 5000,
    );
  }

  /**
   * Get all form inputs, selects, and textareas.
   * @returns {NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>}
   */
  getInputs() {
    return this.querySelectorAll("input, select, textarea");
  }

  /**
   * Get form element.
   * @returns {HTMLFormElement | null}
   */
  getForm() {
    return this.querySelector("form");
  }
}

if (!customElements.get("cart-attributes-form")) {
  customElements.define("cart-attributes-form", CartAttributesForm);
}
